import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { ArticlesAPI } from "../lib/api";
import { ArticleCardMedium, NewsRiverRow } from "../components/ArticleCards";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import Footer from "../components/Footer";

/**
 * TierFeedPage — signed-in-subscriber-only view that filters the feed to the
 * user's paid tier. Route: `/my/:tier` where `:tier` is `premium` or
 * `professional`. Free-tier accounts and unauthenticated visitors are gently
 * bounced to /subscribe.
 *
 * Reuses ArticleCardMedium (top row) + NewsRiverRow (list) so the visual
 * language matches the main Feed page exactly.
 */
export default function TierFeedPage({ tier }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Determine what the user is actually entitled to. `plan` and
  // `subscriptionTier` are kept in sync in the backend but we read both to be
  // safe against timing gaps around Stripe webhook writes.
  const plan = (user?.plan || user?.subscriptionTier || "").toLowerCase();
  const isPremium = plan.startsWith("premium_");
  const isProfessional = plan.startsWith("professional_");
  const subStatus = (user?.subscriptionStatus || "").toLowerCase();
  const active = subStatus === "active" || subStatus === "trialing";

  // Access rules — Professional subscribers can view both tier feeds;
  // Premium subscribers get only the premium feed (per product spec).
  const canView =
    active &&
    ((tier === "premium" && (isPremium || isProfessional)) ||
      (tier === "professional" && isProfessional));

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    ArticlesAPI.list({ readTier: tier, limit: 100 })
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.articles || [];
        setArticles(list);
      })
      .finally(() => setLoading(false));
  }, [tier, canView]);

  const TIER_META = {
    premium: {
      label: "Premium Feed",
      dek: "Individual investor research — the full Premium library, filtered to just your tier.",
      accent: "bg-[#B8E9F1]",
      chipText: "P",
    },
    professional: {
      label: "Professional Feed",
      dek: "Institutional-grade research — every Professional-tier piece in one place.",
      accent: "bg-[#0B1F33]",
      chipText: "Prof",
    },
  };
  const meta = TIER_META[tier] || TIER_META.premium;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FFFFFF]">
        <Header />
        <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
          <Lock className="w-8 h-8 mx-auto text-[#0891B2] mb-4" />
          <h1 className="font-serif-display text-[36px] font-bold text-[#0B1F33]">
            Sign in to view your tier
          </h1>
          <p className="mt-3 text-[15px] text-neutral-700">
            The {tier === "professional" ? "Professional" : "Premium"} feed is
            reserved for subscribers. Sign in with your account to view it.
          </p>
          <Link
            to="/subscribe"
            className="mt-6 inline-block bg-[#0B1F33] text-[#FFFFFF] px-5 py-3 font-condensed uppercase tracking-[0.2em] text-[12px] font-bold hover:bg-[#132f57]"
            data-testid="tier-feed-signup-cta"
          >
            View plans
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-[#FFFFFF]">
        <Header />
        <div className="max-w-[720px] mx-auto px-6 py-24 text-center">
          <Lock className="w-8 h-8 mx-auto text-[#0891B2] mb-4" />
          <h1 className="font-serif-display text-[36px] font-bold text-[#0B1F33]">
            {tier === "professional" ? "Professional" : "Premium"} tier
            required
          </h1>
          <p className="mt-3 text-[15px] text-neutral-700">
            {tier === "professional"
              ? "The Professional feed is a separate tier from Premium. Upgrade to Professional to access institutional research."
              : "You'll need an active Premium or Professional subscription to view this feed."}
          </p>
          <Link
            to="/subscribe"
            className="mt-6 inline-block bg-[#B8E9F1] text-[#0B1F33] px-5 py-3 font-condensed uppercase tracking-[0.2em] text-[12px] font-bold hover:bg-[#9FDDE7]"
            data-testid="tier-feed-upgrade-cta"
          >
            Upgrade to {tier === "professional" ? "Professional" : "Premium"}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const [lead, ...rest] = articles;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <Header />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-neutral-600 hover:text-[#0891B2] font-condensed font-bold mb-4"
          data-testid="tier-feed-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to main feed
        </Link>
        <div className="flex items-center gap-3">
          <span
            className={`inline-block ${meta.accent} text-[${
              tier === "professional" ? "#FFFFFF" : "#0B1F33"
            }] px-2.5 py-1 font-condensed text-[13px] font-bold`}
            style={{ color: tier === "professional" ? "#FFFFFF" : "#0B1F33" }}
          >
            {meta.chipText}
          </span>
          <h1 className="font-serif-display text-[38px] leading-none font-bold text-[#0B1F33]">
            {meta.label}
          </h1>
        </div>
        <p className="mt-3 text-[15px] text-neutral-700 max-w-[720px]">
          {meta.dek}
        </p>
        <div className="mt-8 border-t border-neutral-300" />
        {loading ? (
          <div className="py-16 text-center text-[13px] tracking-[0.2em] uppercase font-condensed font-bold text-neutral-500">
            Loading tier archive…
          </div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center text-[15px] text-neutral-600">
            No {tier} articles are published yet. Check back soon — new pieces
            drop weekly.
          </div>
        ) : (
          <>
            {lead && (
              <div className="mt-8">
                <ArticleCardMedium a={lead} />
              </div>
            )}
            <div className="mt-8">
              {rest.map((a) => (
                <NewsRiverRow key={a.id || a.slug} a={a} />
              ))}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
